import type { CareerReportData } from '@/lib/careerAnalysis';

interface Props {
  report: CareerReportData;
}

function MetricBar({ label, score, level, comment, color }: { label: string; score: number; level: string; comment: string; color: string }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm font-semibold text-foreground-900">{label}</span>
        <span className="text-xs font-bold text-foreground-700">{level} · {score}점</span>
      </div>
      <div className="w-full bg-background-200 rounded-full h-2 mb-2">
        <div className={`${color} h-2 rounded-full transition-all duration-500`} style={{ width: `${Math.min(score, 100)}%` }}></div>
      </div>
      <p className="text-xs text-foreground-500 leading-relaxed">{comment}</p>
    </div>
  );
}

export default function CareerProReport({ report }: Props) {
  return (
    <div className="space-y-4 mb-4">
      {/* 종합 유형 요약 */}
      <div className="bg-background-50 border border-primary-200 rounded-xl p-5">
        <h2 className="font-semibold text-foreground-950 mb-3 flex items-center gap-2">
          <i className="ri-fingerprint-line text-primary-500"></i>
          진로 프로파일 종합 해석
        </h2>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-14 h-14 flex items-center justify-center bg-primary-500 rounded-xl text-white font-bold text-xl flex-shrink-0">
            {report.hollandCode}
          </div>
          <div>
            <div className="text-xs text-foreground-500">나의 진로 유형 코드</div>
            <div className="font-bold text-lg text-foreground-950">{report.codeTitle}</div>
          </div>
        </div>
        <p className="text-sm text-foreground-700 leading-relaxed">{report.codeSummary}</p>
      </div>

      {/* 진단 지표 3종 */}
      <div className="bg-background-50 border border-background-200 rounded-xl p-5">
        <h2 className="font-semibold text-foreground-950 mb-1 flex items-center gap-2">
          <i className="ri-radar-line text-secondary-500"></i>
          진로 진단 지표
        </h2>
        <p className="text-xs text-foreground-500 mb-4">
          검사 결과의 신뢰도와 방향성을 전문 진단 기준(정합도·일관성·변별도)으로 분석했어요.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <MetricBar label="정합도" score={report.congruence.score} level={report.congruence.level} comment={report.congruence.comment} color="bg-primary-500" />
          <MetricBar label="흥미 방향 일관성" score={report.consistency.score} level={report.consistency.level} comment={report.consistency.comment} color="bg-secondary-500" />
          <MetricBar label="변별도" score={report.differentiation.score} level={report.differentiation.level} comment={report.differentiation.comment} color="bg-accent-500" />
        </div>
      </div>

      {/* 강점 & 개발 영역 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-background-50 border border-primary-200 rounded-xl p-5">
          <h2 className="font-semibold text-foreground-950 mb-3 flex items-center gap-2">
            <i className="ri-medal-2-line text-primary-500"></i>
            핵심 강점 역량
          </h2>
          <div className="space-y-3">
            {report.strengths.map((s) => (
              <div key={s.name} className="flex items-start gap-2.5">
                <span className="w-6 h-6 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg flex-shrink-0 mt-0.5">
                  <i className="ri-check-line text-sm"></i>
                </span>
                <div>
                  <div className="text-sm font-semibold text-foreground-900">{s.name}</div>
                  <p className="text-xs text-foreground-600 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-background-50 border border-accent-200 rounded-xl p-5">
          <h2 className="font-semibold text-foreground-950 mb-3 flex items-center gap-2">
            <i className="ri-seedling-line text-accent-500"></i>
            성장·보완 영역
          </h2>
          <div className="space-y-3">
            {report.developments.map((d) => (
              <div key={d.name} className="flex items-start gap-2.5">
                <span className="w-6 h-6 flex items-center justify-center bg-accent-100 text-accent-600 rounded-lg flex-shrink-0 mt-0.5">
                  <i className="ri-arrow-up-line text-sm"></i>
                </span>
                <div>
                  <div className="text-sm font-semibold text-foreground-900">{d.name}</div>
                  <p className="text-xs text-foreground-600 leading-relaxed">{d.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 학습 전략 */}
      <div className="bg-background-50 border border-background-200 rounded-xl p-5">
        <h2 className="font-semibold text-foreground-950 mb-3 flex items-center gap-2">
          <i className="ri-lightbulb-line text-secondary-500"></i>
          나에게 맞는 학습 전략
        </h2>
        <div className="space-y-2">
          {report.learningStrategy.map((s, i) => (
            <div key={i} className="flex items-start gap-2 bg-secondary-50 border border-secondary-100 rounded-lg p-3">
              <i className="ri-bookmark-line text-secondary-500 mt-0.5 flex-shrink-0"></i>
              <p className="text-sm text-foreground-700 leading-relaxed">{s}</p>
            </div>
          ))}
        </div>
      </div>

      {/* 진로 로드맵 */}
      <div className="bg-background-50 border border-primary-200 rounded-xl p-5">
        <h2 className="font-semibold text-foreground-950 mb-1 flex items-center gap-2">
          <i className="ri-guide-line text-primary-500"></i>
          단계별 진로 로드맵
        </h2>
        <p className="text-xs text-foreground-500 mb-4">중학교부터 고등학교까지, 시기별로 무엇을 준비하면 좋은지 정리했어요.</p>
        <div className="relative pl-6">
          <div className="absolute left-2 top-1 bottom-1 w-px bg-primary-200"></div>
          <div className="space-y-4">
            {report.roadmap.map((phase, idx) => (
              <div key={idx} className="relative">
                <span className="absolute -left-[18px] top-1 w-3.5 h-3.5 bg-primary-500 rounded-full border-2 border-background-50"></span>
                <div className="bg-primary-50 border border-primary-100 rounded-xl p-4">
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                    <span className="text-sm font-bold text-primary-800">{phase.phase}</span>
                    <span className="text-xs bg-primary-500 text-white px-2 py-0.5 rounded-full whitespace-nowrap">{phase.focus}</span>
                  </div>
                  <ul className="mt-2 space-y-1.5">
                    {phase.activities.map((a, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-foreground-700">
                        <i className="ri-checkbox-circle-line text-primary-400 mt-0.5 flex-shrink-0"></i>
                        <span className="leading-relaxed">{a}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 추천 활동 & 자격증 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-background-50 border border-background-200 rounded-xl p-5">
          <h2 className="font-semibold text-foreground-950 mb-3 flex items-center gap-2">
            <i className="ri-flag-2-line text-primary-500"></i>
            추천 비교과·체험 활동
          </h2>
          <div className="flex flex-wrap gap-2">
            {report.activities.map((a) => (
              <span key={a} className="text-xs bg-primary-50 text-primary-700 border border-primary-200 px-2.5 py-1.5 rounded-full">{a}</span>
            ))}
          </div>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-xl p-5">
          <h2 className="font-semibold text-foreground-950 mb-3 flex items-center gap-2">
            <i className="ri-award-line text-accent-500"></i>
            도전해볼 자격·인증
          </h2>
          <div className="flex flex-wrap gap-2">
            {report.certificates.map((c) => (
              <span key={c} className="text-xs bg-accent-50 text-accent-700 border border-accent-200 px-2.5 py-1.5 rounded-full">{c}</span>
            ))}
          </div>
        </div>
      </div>

      {/* 추천 취미 & 특기 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-background-50 border border-secondary-200 rounded-xl p-5">
          <h2 className="font-semibold text-foreground-950 mb-3 flex items-center gap-2">
            <i className="ri-palette-line text-secondary-500"></i>
            나에게 어울리는 취미
          </h2>
          <div className="flex flex-wrap gap-2">
            {report.hobbies.map((h) => (
              <span key={h} className="text-xs bg-secondary-50 text-secondary-800 border border-secondary-200 px-2.5 py-1.5 rounded-full">{h}</span>
            ))}
          </div>
        </div>
        <div className="bg-background-50 border border-secondary-200 rounded-xl p-5">
          <h2 className="font-semibold text-foreground-950 mb-3 flex items-center gap-2">
            <i className="ri-sparkling-line text-secondary-500"></i>
            길러보면 좋은 특기·재능
          </h2>
          <div className="flex flex-wrap gap-2">
            {report.talents.map((t) => (
              <span key={t} className="text-xs bg-secondary-50 text-secondary-800 border border-secondary-200 px-2.5 py-1.5 rounded-full">{t}</span>
            ))}
          </div>
        </div>
      </div>

      {/* 유의사항 */}
      <div className="bg-background-100 border border-background-200 rounded-xl p-4">
        <div className="flex items-start gap-2">
          <i className="ri-information-line text-foreground-400 flex-shrink-0 mt-0.5"></i>
          <div className="space-y-1.5">
            {report.cautions.map((c, i) => (
              <p key={i} className="text-xs text-foreground-500 leading-relaxed">{c}</p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}